// ---------------------------------------------------------------------------
// UX-003 Phase 2 — Mobile-First Design CQRS Event Catalog (vocabulary tier)
// ---------------------------------------------------------------------------
// Story UX-003 (mobile-first design lens): operator-facing surfaces MUST
// load in a mobile-first viewport posture and react to responsive breakpoint
// transitions so the analytics MV can quantify which surfaces are actually
// served mobile-first (vs. desktop-only regressions).
//
// This Phase 2 file locks the CQRS event types the future UX-003 closure
// tranche will emit so the audit-runner, analytics MV, and DLQ consumers
// read one source of truth instead of hand-rolling
// `ui.mobile_view_loaded@v1` and the breakpoint-transition envelope.
//
// Per Governance Rule #7 ("Event Catalog"): every event type catalogued
// BEFORE Phase 3 route implementation, so the future emitter (SPA root
// layout + ResizeObserver/matchMedia breakpoint hook) emits structured
// events matching the locked breakpoint vocabulary.
//
// What this file is NOT:
//  - It does not emit events — that's the future UX-003 closure tranche.
//  - It does not implement the breakpoint hook or root-layout wiring.
//  - It does not declare any React component.
//
// What it IS:
//  - The single source of truth for the 2 UX-003 mobile-first event strings.
//  - Closed catalog of Tailwind-aligned breakpoints (base/sm/md/lg/xl/2xl)
//    covering every viewport width so the emitter cannot drift into
//    ad-hoc names or coerce sub-640px sessions into the wrong bucket.
//  - Typed payload contracts so the emitter + DLQ consumer cannot drift.
//  - Runtime narrowing guards so DLQ/audit-runner reject unknown event types
//    or breakpoint values without any-casting.
// ---------------------------------------------------------------------------

/**
 * Closed catalog of the 2 UX-003 CQRS event types. Versioned with the `@v1`
 * suffix per platform convention (Governance Rule #7).
 *
 * One business fact per event (Governance Rule #6):
 *  - `mobile_view_loaded` — the SPA root layout mounted and the initial
 *    viewport was sampled. Emitted once per session/route entry.
 *  - `responsive_breakpoint_changed` — the matchMedia/ResizeObserver loop
 *    observed a transition from one closed-vocab breakpoint to another.
 */
export const UX_003_MOBILE_FIRST_EVENT_TYPES = [
  "ui.mobile_view_loaded@v1",
  "ui.responsive_breakpoint_changed@v1",
] as const;

export type Ux003MobileFirstEventType =
  (typeof UX_003_MOBILE_FIRST_EVENT_TYPES)[number];

/**
 * Closed catalog of responsive breakpoints. Mirrors the Tailwind 4 default
 * breakpoint scale FULLY so the emitter, audit MV, and analytics
 * dashboards share one vocabulary that can represent every viewport
 * width without coercion. Casing variants and ad-hoc pixel widths MUST
 * NOT be emitted — only one of these tokens.
 *
 * For a "mobile-first viewport posture" signal, every sub-640px session
 * MUST land in `base` (the unprefixed Tailwind mobile bucket). Dropping
 * those into `sm` would systematically over-count "small tablet" and
 * under-count "phone" — the exact analytics distortion this catalog
 * exists to prevent. Likewise, every `>= 1536px` session belongs in
 * `2xl`, the top Tailwind default bucket; binning those into `xl` would
 * mask widescreen-only regressions.
 *
 *  - `base` — unprefixed mobile (< 640px); the Tailwind "no-prefix"
 *             bucket and the dominant mobile-first session class.
 *  - `sm`   — small (>= 640px and < 768px).
 *  - `md`   — medium (>= 768px and < 1024px).
 *  - `lg`   — large (>= 1024px and < 1280px).
 *  - `xl`   — extra-large (>= 1280px and < 1536px).
 *  - `2xl`  — extra-extra-large (>= 1536px); Tailwind's top default
 *             breakpoint, common on widescreen desktops + 4K monitors.
 *
 * Append-only. Renaming silently breaks UX-003 analytics rollups.
 */
export const UX_003_BREAKPOINT_VOCAB = [
  "base",
  "sm",
  "md",
  "lg",
  "xl",
  "2xl",
] as const;

export type Ux003BreakpointVocab = (typeof UX_003_BREAKPOINT_VOCAB)[number];

/**
 * Lower-bound pixel widths for each catalogued breakpoint, sourced from
 * the Tailwind 4 default scale. The emitter MUST classify a viewport
 * `width` by walking these thresholds in descending order and picking
 * the first bucket where `width >= bound`. Pinned in the catalog so the
 * emitter, audit-runner, and analytics MV agree on the boundaries.
 * Changing a bound MUST go via a new event-type version (`@v2`).
 */
export const UX_003_BREAKPOINT_LOWER_BOUNDS_PX: Readonly<
  Record<Ux003BreakpointVocab, number>
> = Object.freeze({
  base: 0,
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  "2xl": 1536,
});

/**
 * Classifies a non-negative viewport width (CSS pixels) into the
 * matching catalog bucket. Pinned in the catalog so every emitter
 * (initial mount + ResizeObserver/matchMedia transitions) shares one
 * implementation — drift here would silently shift the analytics
 * boundaries.
 *
 * Returns `null` for negative or non-finite inputs so callers can fail
 * fast instead of emitting an event with a coerced bucket.
 */
export function classifyUx003ViewportWidth(
  widthPx: number,
): Ux003BreakpointVocab | null {
  if (!Number.isFinite(widthPx) || widthPx < 0) return null;
  // Walk descending so the largest matching bucket wins.
  if (widthPx >= UX_003_BREAKPOINT_LOWER_BOUNDS_PX["2xl"]) return "2xl";
  if (widthPx >= UX_003_BREAKPOINT_LOWER_BOUNDS_PX.xl) return "xl";
  if (widthPx >= UX_003_BREAKPOINT_LOWER_BOUNDS_PX.lg) return "lg";
  if (widthPx >= UX_003_BREAKPOINT_LOWER_BOUNDS_PX.md) return "md";
  if (widthPx >= UX_003_BREAKPOINT_LOWER_BOUNDS_PX.sm) return "sm";
  return "base";
}

/* -------------------------------------------------------------------------- */
/* Runtime narrowing guards                                                    */
/* -------------------------------------------------------------------------- */

export function isUx003MobileFirstEventType(
  value: unknown,
): value is Ux003MobileFirstEventType {
  return (
    typeof value === "string" &&
    (UX_003_MOBILE_FIRST_EVENT_TYPES as readonly string[]).includes(value)
  );
}

export function isUx003BreakpointVocab(
  value: unknown,
): value is Ux003BreakpointVocab {
  return (
    typeof value === "string" &&
    (UX_003_BREAKPOINT_VOCAB as readonly string[]).includes(value)
  );
}

/* -------------------------------------------------------------------------- */
/* Per-event payload contracts                                                 */
/* -------------------------------------------------------------------------- */

export interface Ux003MobileViewLoadedPayload {
  /** Tenant scope for fan-out + isolation. */
  tenantId: string;
  /** Session id from the SPA — coupled to the operator's user-session. */
  sessionId: string;
  /** SPA route path the layout mounted under (e.g. `/dashboard`). */
  routePath: string;
  /** Initial breakpoint observed at mount (closed vocab). */
  initialBreakpoint: Ux003BreakpointVocab;
  /** Viewport width in CSS pixels at mount (>= 0). */
  viewportWidth: number;
  /** ISO-8601 — when the layout mounted and sampled the viewport. */
  loadedAt: string;
}

export interface Ux003ResponsiveBreakpointChangedPayload {
  /** Tenant scope for fan-out + isolation. */
  tenantId: string;
  /** Session id from the SPA. */
  sessionId: string;
  /** SPA route path the transition occurred under. */
  routePath: string;
  /** Previous breakpoint observed (closed vocab). */
  prevBreakpoint: Ux003BreakpointVocab;
  /** New breakpoint observed (closed vocab). */
  nextBreakpoint: Ux003BreakpointVocab;
  /** Viewport width in CSS pixels at the transition (>= 0). */
  viewportWidth: number;
  /** ISO-8601 — when the SPA observed the transition. */
  changedAt: string;
}

/**
 * Discriminated union mapping each event type to its payload. Consumers
 * (audit-runner, analytics MV, DLQ) `switch (event.type)` and pull the
 * right payload without any-casting.
 */
export type Ux003MobileFirstEvent =
  | {
      type: "ui.mobile_view_loaded@v1";
      payload: Ux003MobileViewLoadedPayload;
    }
  | {
      type: "ui.responsive_breakpoint_changed@v1";
      payload: Ux003ResponsiveBreakpointChangedPayload;
    };
